import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormHelperText,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Typography,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Category as CategoryIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { AddEditFeaturesList, getFeaturesDropdown, getSubCategory } from 'src/ApiServices';
import { toast } from 'react-toastify';
import palette from 'src/theme/palette';
import useMediaQuery from '@mui/material/useMediaQuery';

const AddEditFeaturesListModal = ({ open, onClose, onSuccess, feature }) => {
  const [formData, setFormData] = useState({
    name: '',
    feature_category_id: '',
    sub_feature_category_id: '',
    id: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    if (open) {
      fetchDropdown();
      if (feature) {
        setFormData({
          name: feature.name || '',
          feature_category_id: feature.feature_category_id || '',
          sub_feature_category_id: feature.sub_feature_category_id || '',
          id: feature.id || ''
        });
        setImagePreview(feature.image || null);

        // Fetch sub-categories if feature has category
        if (feature.feature_category_id) {
          fetchSubCategories(feature.feature_category_id);
        }
      } else {
        resetForm();
      }
    }
  }, [open, feature]);

  const fetchDropdown = async () => {
    try {
      const data = await getFeaturesDropdown();
      setCategories(data?.data || []);
    } catch (error) {
      toast.error('Failed to load feature categories!');
    }
  };

  const fetchSubCategories = async (categoryId) => {
    if (!categoryId) {
      setSubCategories([]);
      setFormData((prev) => ({ ...prev, sub_feature_category_id: '' }));
      return;
    }

    setLoadingSubCategories(true);
    try {
      const data = await getSubCategory(categoryId);
      setSubCategories(data?.data || []);
    } catch (error) {
      console.error('Failed to load sub-categories!');
      setSubCategories([]);
    } finally {
      setLoadingSubCategories(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', feature_category_id: '', sub_feature_category_id: '', id: '' });
    setImageFile(null);
    setImagePreview(null);
    setErrors({});
    setSubCategories([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updatedFormData = { ...prev, [name]: value };

      // If category changes, fetch sub-categories and reset sub-category selection
      if (name === 'feature_category_id') {
        fetchSubCategories(value);
        updatedFormData.sub_feature_category_id = '';
      }

      return updatedFormData;
    });
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
    setErrors((prev) => ({ ...prev, feature_image: '' }));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    const fileInput = document.getElementById('feature-image-upload');
    if (fileInput) fileInput.value = '';
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Feature name is required';
    if (!formData.feature_category_id)
      newErrors.feature_category_id = 'Feature category is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      const payload = new FormData();
      payload.append('name', formData.name.trim());
      payload.append('feature_category_id', formData.feature_category_id);

      // Add sub_feature_category_id if it has a value
      if (formData.sub_feature_category_id) {
        payload.append('sub_feature_category_id', formData.sub_feature_category_id);
      }

      if (formData.id) {
        payload.append('id', formData.id);
      }

      if (imageFile) {
        payload.append('feature_image', imageFile);
      }

      await AddEditFeaturesList(payload);
      toast.success(`Feature ${feature ? 'updated' : 'created'} successfully!`);
      onSuccess();
      handleClose();
    } catch (error) {
      toast.error(
        error.response?.data?.message || `Failed to ${feature ? 'update' : 'create'} feature!`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100vh' : '90vh',
          width: isMobile ? '100vw' : '1000px',
          margin: isMobile ? 0 : undefined
        }
      }}
    >
      <DialogTitle sx={{ backgroundColor: palette.primary.main, color: 'white', py: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <CategoryIcon />
            <Typography variant="h6" fontFamily="poppinsSemiBold">
              {feature ? 'Edit Feature' : 'Add New Feature'}
            </Typography>
          </Box>
          <Button onClick={handleClose} size="small" sx={{ minWidth: 'auto', color: 'white' }}>
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Feature Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                required
                disabled={loading}
              />
              {errors.name && <FormHelperText error>{errors.name}</FormHelperText>}
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.feature_category_id} disabled={loading}>
                <InputLabel>Feature Category *</InputLabel>
                <Select
                  name="feature_category_id"
                  value={formData.feature_category_id}
                  onChange={handleInputChange}
                  label="Feature Category *"
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.feature_category_id && (
                  <FormHelperText error>{errors.feature_category_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                disabled={loading || loadingSubCategories || !formData.feature_category_id}
              >
                <InputLabel>Sub Category (Optional)</InputLabel>
                <Select
                  name="sub_feature_category_id"
                  value={formData.sub_feature_category_id}
                  onChange={handleInputChange}
                  label="Sub Category (Optional)"
                >
                  <MenuItem value="">None</MenuItem>
                  {subCategories.map((subCat) => (
                    <MenuItem key={subCat.id} value={subCat.id}>
                      {subCat.name}
                    </MenuItem>
                  ))}
                </Select>
                {loadingSubCategories && <FormHelperText>Loading sub-categories...</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontFamily: 'poppinsMedium' }}>
                Feature Image (Optional)
              </Typography>

              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: imagePreview ? 'success.main' : 'grey.300',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: imagePreview ? 'action.hover' : 'grey.50'
                }}
              >
                {imagePreview ? (
                  <Box>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        objectFit: 'contain',
                        borderRadius: '8px'
                      }}
                    />
                    <Box mt={2} display="flex" gap={1} justifyContent="center">
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        size="small"
                        disabled={loading}
                      >
                        Change Image
                        <input
                          type="file"
                          hidden
                          id="feature-image-upload"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleRemoveImage}
                        disabled={loading}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                    <Typography variant="body2" color="grey.600" gutterBottom>
                      Click to upload an image
                    </Typography>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      sx={{ mt: 1 }}
                      disabled={loading}
                    >
                      Upload Image
                      <input
                        type="file"
                        hidden
                        id="feature-image-upload"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </Button>
                    <Typography variant="caption" color="grey.500" display="block" sx={{ mt: 1 }}>
                      JPEG, PNG, GIF, WebP • Max 5MB
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} variant="outlined" disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{ backgroundColor: palette.primary.main }}
            disabled={loading}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                <span>Saving...</span>
              </Box>
            ) : feature ? (
              'Update Feature'
            ) : (
              'Add Feature'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddEditFeaturesListModal;
